import {
  GroupFilterSelected,
  GroupFilterType
} from "../../../components/BookingForm/BookingFilter/types/GroupFilters.type";
import { IPlaceCategory } from "../../../components/FacilityManager/Domain/Types/FloorPlan/PlaceCategory.type";
import { IZoneCategory } from "../../../components/FacilityManager/Domain/Types/FloorPlan/ZoneCategory.type";
import { BookingProperty } from "../../../components/FacilityManager/Domain/Types/FloorPlan/BookingProperty.type";
import { EquipmentCategory } from "../../../components/FacilityManager/Domain/Types/FloorPlan/EquipmentCategory.type";
import {
  BookingInputsFilter,
  BookingType,
  NotSerializedBookingInputs
} from "../typings/booking-inputs";

export type EquipmentAssignmentFields = {
  id: number;
  name: string;
  placeInventoryId?: number | null;
  zoneInventoryId?: number | null;
};

export function isAssignedEquipmentInventory(equipment: EquipmentAssignmentFields): boolean {
  return equipment.placeInventoryId != null || equipment.zoneInventoryId != null;
}

export function assignedEquipmentFilterEntries(
  equipmentInventory: EquipmentAssignmentFields[]
): { id: number; name: string }[] {
  return equipmentInventory
    .filter(isAssignedEquipmentInventory)
    .map(({ id, name }) => ({ id, name }));
}

/** Location ids used to scope early equipment filters to the user's bookable locations. */
export function resolveBookableLocationIdsForEquipmentFilter(
  preferredLocationIds: readonly string[] | undefined,
  companyLocations: { id: number }[]
): number[] {
  if (preferredLocationIds?.length) {
    return preferredLocationIds.map(Number).filter(Number.isFinite);
  }
  return companyLocations.map(location => location.id);
}

export function equipmentFilterEntriesFromLocationScopedInventory(
  equipmentInventory: { id?: number; name: string }[]
): { id: number; name: string }[] {
  const seen = new Set<number>();
  const entries: { id: number; name: string }[] = [];

  for (const item of equipmentInventory) {
    if (item.id == null || seen.has(item.id)) {
      continue;
    }
    seen.add(item.id);
    entries.push({ id: item.id, name: item.name });
  }

  return entries;
}

export function hasActiveBookingFilters(bookingFilters?: BookingInputsFilter): boolean {
  if (!bookingFilters) {
    return false;
  }
  const { propertyIds, equipmentIds, equipmentCategories, placeCategoryIds, zoneCategoryIds } =
    bookingFilters;
  return (
    (propertyIds?.length ?? 0) > 0 ||
    (equipmentIds?.length ?? 0) > 0 ||
    (equipmentCategories?.length ?? 0) > 0 ||
    (placeCategoryIds?.length ?? 0) > 0 ||
    (zoneCategoryIds?.length ?? 0) > 0
  );
}

export function activeBookingFiltersForApi(
  bookingFilters?: BookingInputsFilter
): BookingInputsFilter | undefined {
  return hasActiveBookingFilters(bookingFilters) ? bookingFilters : undefined;
}

type EarlyInventoryFilterInputs = Pick<NotSerializedBookingInputs, "activityBasedBooking" | "mode">;

/** Whether the date/time step shows the early inventory filter panel. */
export function shouldShowEarlyInventoryFilters(inputs: EarlyInventoryFilterInputs): boolean {
  if (inputs.activityBasedBooking) {
    return false;
  }
  return inputs.mode !== BookingType.VEHICLE && inputs.mode !== BookingType.VISITOR;
}

/**
 * Preferred place categories may still auto-apply on the floor plan when the early filter
 * step is not shown. When it is shown, empty bookingFilters means the user chose no filters.
 */
export function shouldApplyInitialPreferredPlaceCategoryFilter(
  inputs: EarlyInventoryFilterInputs,
  hasActiveFilters: boolean
): boolean {
  if (hasActiveFilters) {
    return false;
  }
  return !shouldShowEarlyInventoryFilters(inputs);
}

export function countActiveBookingFilterSelections(bookingFilters?: BookingInputsFilter): number {
  if (!bookingFilters) {
    return 0;
  }
  const { propertyIds, equipmentIds, equipmentCategories, placeCategoryIds, zoneCategoryIds } =
    bookingFilters;
  return (
    (propertyIds?.length ?? 0) +
    (equipmentIds?.length ?? 0) +
    (equipmentCategories?.length ?? 0) +
    (placeCategoryIds?.length ?? 0) +
    (zoneCategoryIds?.length ?? 0)
  );
}

export function countActiveGroupFilterItems(filterItems: GroupFilterSelected[]): number {
  return countActiveBookingFilterSelections(buildBookingFiltersFromGroupFilterItems(filterItems));
}

export function buildBookingFiltersFromGroupFilterItems(
  filterItems: GroupFilterSelected[]
): BookingInputsFilter {
  const equipCate = filterItems.filter(item => item.type === GroupFilterType.EQUIPMENTCATEGORY);
  const equipment = filterItems.filter(item => item.type === GroupFilterType.EQUIPMENT);
  const placeCate = filterItems.filter(item => item.type === GroupFilterType.PLACECATEGORY);
  const zoneCate = filterItems.filter(item => item.type === GroupFilterType.ZONECATEGORY);
  const bookProp = filterItems.filter(item => item.type === GroupFilterType.BOOKINGPROPERTY);

  return {
    equipmentIds: equipment.map(item => Number(item.id)),
    equipmentCategories: equipCate.map(c => ({
      id: Number(c.id),
      ...(c.quantity != null ? { quantity: Number(c.quantity) } : {})
    })),
    placeCategoryIds: placeCate.map(c => Number(c.id)),
    zoneCategoryIds: zoneCate.map(c => Number(c.id)),
    propertyIds: bookProp.map(p => Number(p.id))
  };
}

export type BookingFilterCatalogs = {
  placeCategories?: IPlaceCategory[];
  zoneCategories?: IZoneCategory[];
  bookingProperties?: BookingProperty[];
  equipmentCategories?: EquipmentCategory[];
  equipment?: { id: number; name: string }[];
};

export function groupFilterItemsFromBookingFilters(
  bookingFilters: BookingInputsFilter,
  catalogs: BookingFilterCatalogs
): GroupFilterSelected[] {
  const items: GroupFilterSelected[] = [];

  bookingFilters.placeCategoryIds?.forEach(id => {
    const category = catalogs.placeCategories?.find(c => c.id === id);
    items.push({
      type: GroupFilterType.PLACECATEGORY,
      id,
      name: category?.name ?? String(id),
      checked: true
    });
  });

  bookingFilters.zoneCategoryIds?.forEach(id => {
    const category = catalogs.zoneCategories?.find(c => c.id === id);
    items.push({
      type: GroupFilterType.ZONECATEGORY,
      id,
      name: category?.name ?? String(id),
      checked: true
    });
  });

  bookingFilters.propertyIds?.forEach(id => {
    const property = catalogs.bookingProperties?.find(p => p.id === id);
    items.push({
      type: GroupFilterType.BOOKINGPROPERTY,
      id,
      name: property?.name ?? String(id),
      checked: true
    });
  });

  bookingFilters.equipmentCategories?.forEach(filter => {
    if (filter.id == null) {
      return;
    }
    const category = catalogs.equipmentCategories?.find(c => c.id === filter.id);
    items.push({
      type: GroupFilterType.EQUIPMENTCATEGORY,
      id: filter.id,
      name: category?.name ?? String(filter.id),
      checked: true,
      ...(filter.quantity != null ? { quantity: filter.quantity } : {})
    });
  });

  bookingFilters.equipmentIds?.forEach(id => {
    const equipmentItem = catalogs.equipment?.find(item => item.id === id);
    items.push({
      type: GroupFilterType.EQUIPMENT,
      id,
      name: equipmentItem?.name ?? String(id),
      checked: true
    });
  });

  return items;
}

function sortedNumericIds(ids?: number[]): number[] {
  return [...(ids ?? [])].sort((a, b) => a - b);
}

function normalizeEquipmentCategoriesForCompare(
  categories?: { id?: number; quantity?: number }[]
): { id: number; quantity?: number }[] {
  return [...(categories ?? [])]
    .map(category => ({
      id: Number(category.id),
      ...(category.quantity != null ? { quantity: Number(category.quantity) } : {})
    }))
    .sort((a, b) => a.id - b.id);
}

export function filterItemsReflectBookingFilters(
  filterItems: GroupFilterSelected[],
  bookingFilters: BookingInputsFilter
): boolean {
  const fromItems = buildBookingFiltersFromGroupFilterItems(filterItems);

  return (
    JSON.stringify(sortedNumericIds(fromItems.placeCategoryIds)) ===
      JSON.stringify(sortedNumericIds(bookingFilters.placeCategoryIds)) &&
    JSON.stringify(sortedNumericIds(fromItems.zoneCategoryIds)) ===
      JSON.stringify(sortedNumericIds(bookingFilters.zoneCategoryIds)) &&
    JSON.stringify(sortedNumericIds(fromItems.propertyIds)) ===
      JSON.stringify(sortedNumericIds(bookingFilters.propertyIds)) &&
    JSON.stringify(sortedNumericIds(fromItems.equipmentIds)) ===
      JSON.stringify(sortedNumericIds(bookingFilters.equipmentIds)) &&
    JSON.stringify(normalizeEquipmentCategoriesForCompare(fromItems.equipmentCategories)) ===
      JSON.stringify(normalizeEquipmentCategoriesForCompare(bookingFilters.equipmentCategories))
  );
}

export function areBookingFilterCatalogsReady(
  bookingFilters: BookingInputsFilter,
  usedCategory: { type: "place" | "zone"; options: unknown[] }
): boolean {
  const placeCategoryIds = bookingFilters.placeCategoryIds ?? [];
  const zoneCategoryIds = bookingFilters.zoneCategoryIds ?? [];
  const catalogsReadyForPlace =
    placeCategoryIds.length === 0 ||
    (usedCategory.type === "place" && usedCategory.options.length > 0);
  const catalogsReadyForZone =
    zoneCategoryIds.length === 0 ||
    (usedCategory.type === "zone" && usedCategory.options.length > 0);

  return catalogsReadyForPlace && catalogsReadyForZone;
}
